"use client";

import { modelDynamicTopContent, modelLocationTopContent, modelLocationTopDescriptionContent } from "@/app/[brand]/[model]/pageStaticData";
import { formatPrice, formatPriceRange, dateFormat } from "@/app/utilities/helper";
import { ProductDataProps } from "@/app/utilities/interfaces";
import LeadButton from "@/component/ui/Lead/LeadButton";
import SliderWithThumbnails from "@/component/pdp/SliderWithThumbnail";
import React, { useState, useEffect, useRef, useMemo } from "react";
import Image from "next/image";
import BodyContent from "../ui/BodyContent/BodyContent";
import { getVariantList, getAllUserReviews } from '@/app/utilities/apis';
import VariantSelect, { Variant } from "./VariantSelect";

import BookTestDrive from "./BookTestDrive";
import { convertModelTags, findSpecValue } from "@/app/utilities/modelTagConverter";
import EmiStartCard from "./EmiStartCard";
import { useEmiData } from "@/hooks/useEmiData";
import { useRTOPricingData } from "@/hooks/useRTOPricingData";
import { PDP_EMICALCULATOR_POPUP_TRACKING, PRODUCT_DETAIL_TRACKING } from "@/lib/constants/tracking";
import Link from "next/link";
import { useRouter } from "next/navigation";
import dynamic from "next/dynamic";
import { getOnRoadPrice } from "@/utils/onRoadPrice";
import type { ImagePageGalleryData } from "@/component/pdp/image-page/types";
import MobileStickyCta, { MOBILE_STICKY_CTA_CLASS } from "@/component/pdp/MobileStickyCta";

// dynamic imports
const EmiCalculator = dynamic(() => import("../ui/EmiCalculator/EmiCalculator"));
const LocationSelectModal = dynamic(() => import("../LocationSelectModal"));

interface HeroSectionMobileProps extends ProductDataProps {
    priority?: boolean;
    isMobile?: boolean;
    gorpTrackingClass?: string;
    isPriceInCityRoute?: boolean;
    variantSlug?: string;
    GORPFormHeading?: React.ReactNode;
    h1Title?: string;
    titleTag?: "h1" | "h2";
    topDescription?: string;
    useSplashPopup?: boolean;
    // Prefetched server values (optional)
    prefetchedReviewRating?: number;
    prefetchedReviewCount?: number;
    prefetchedRtoResult?: any;
    prefetchedInsuranceResult?: any;
    prefetchedVariantId?: string | number;
    prefetchedCity?: string;
    isSpecPage?: boolean;
    hideReviewRating?: boolean;
    leadCTAText?: string;
    is_discontinued?: boolean;
    EmiStartCardTracking?: string;
    prefetchedCitySlug?: string;
    isPDPRoute?: boolean;
    prefetchedVariantRes?: any;
    enableOnRoadPricing?: boolean;
    galleryData?: ImagePageGalleryData;
}

const parseEngineCc = (cc: any): number | undefined => {
    if (!cc) return undefined;
    const n = parseInt(String(cc).replace(/\D/g, ''), 10);
    return isNaN(n) ? undefined : n;
};

const getPrefetchedVariants = (prefetchedVariantRes: any) => {
    if (Array.isArray(prefetchedVariantRes)) return prefetchedVariantRes;
    if (Array.isArray(prefetchedVariantRes?.data)) return prefetchedVariantRes.data;
    return [];
};

const HeroSectionMobile: React.FC<HeroSectionMobileProps> = ({
    data,
    priority = false,
    isMobile = false,
    gorpTrackingClass = "",
    isPriceInCityRoute = false,
    isVariantRoute = false,
    variantSlug = '',
    GORPFormHeading,
    h1Title,
    titleTag = "h1",
    topDescription,
    useSplashPopup = false,
    prefetchedReviewRating,
    prefetchedReviewCount,
    prefetchedRtoResult,
    prefetchedInsuranceResult,
    prefetchedVariantId,
    prefetchedCity,
    isSpecPage,
    hideReviewRating = false,
    leadCTAText,
    is_discontinued,
    EmiStartCardTracking,
    prefetchedCitySlug,
    isPDPRoute = false,
    prefetchedVariantRes,
    enableOnRoadPricing = true,
    galleryData,
}) => {
    const model = (data as any)?.modelData || data as any;

    const variant = Array.isArray(model?.base_variant) ? model?.base_variant[0] : model?.base_variant;
    const prefetchedVariants = useMemo(() => getPrefetchedVariants(prefetchedVariantRes), [prefetchedVariantRes]);

    const [variants, setVariants] = useState<any[]>(prefetchedVariants.length > 0 ? prefetchedVariants : Array.isArray(model?.base_variant) ? model.base_variant : (model?.base_variant ? [model.base_variant] : (Array.isArray(model?.variants) ? model.variants : [])));

    const [selectedVariant, setSelectedVariant] = useState<any>(variant);
    const [showVariantDropdown, setShowVariantDropdown] = useState(false);
    const [reviewRating, setReviewRating] = useState<number>(Number(prefetchedReviewRating ?? model?.user_rating ?? 0));
    const [reviewCount, setReviewCount] = useState<number>(Number(prefetchedReviewCount ?? model?.reviews_count ?? 0));
    const [isFetchingReviews, setIsFetchingReviews] = useState<boolean>(false);
    const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
    const variantDropdownRef = useRef<HTMLDivElement>(null);
    const offerBtnRef = useRef<HTMLDivElement>(null);
    const router = useRouter();

    const saveSelVariantCookie = (modelSlug: string, variantSlug: string) => {
        if (!modelSlug || !variantSlug) return;
        document.cookie = `_selVariant=${encodeURIComponent(`${modelSlug}:${variantSlug}`)};path=/;max-age=${60 * 60 * 24 * 30}`;
    };

    const extractCcAndFuel = (v: any) => {
        if (!v) return { cc: '', fuel: '' };
        return {
            fuel: v?.fuel_types || findSpecValue(v, 'fuel_types') || '',

            cc: v?.engine || v?.engine_displacement || findSpecValue(v, 'engine_displacement') || ''
        };
    };

    const isEmptyDescription = (desc: string) =>
        !desc || ["<p><br></p>", "<p></p>", "<br>", ""].includes(desc.trim());

    const isDiscontinued = is_discontinued || model?.status === 'discontinued';

    const price = model?.price_range?.base || selectedVariant?.price || variant?.price || 0;
    const selectedInfo = extractCcAndFuel(selectedVariant);

    const { rtoResult, insuranceResult, isRtoLoading, isInsuranceLoading, selectedCity, setSelectedCity } = useRTOPricingData({
        model,
        selectedVariant,
        price,
        isPriceInCityRoute,
        prefetchedRtoResult,
        prefetchedInsuranceResult,
        prefetchedVariantId,
        prefetchedCity,
        extraDeps: [selectedInfo.cc],
        syncCityFromCookieOnMount: true,
    });

    const isElectric = Array.isArray(model?.fuel_types) ? model.fuel_types.some((type: string) => type?.toLowerCase() === 'electric') : false;
    const isUpcoming = model?.status === 'upcoming';
    const rating = reviewRating;
    const reviews = reviewCount;
    const displayPrice = formatPriceRange(model?.price_range, true);
    const hasDisplayPrice = Boolean(model?.price_range?.base || model?.price_range?.top || selectedVariant?.price || variant?.price);

    const min_price_variant = Array.isArray(model?.variants) ? model.variants.reduce((min: any, v: any) => {
        const vPrice = Number(v.price ?? 0);
        if (vPrice < (min.price ?? Infinity)) {
            return v;
        }
        return min;
    }, model.variants[0]) : null;

    let min_onRoad_price = 0;

    if (min_price_variant && model?.locationInfo && isPriceInCityRoute && enableOnRoadPricing) {
        const minPriceVariantOnRoad = getOnRoadPrice(min_price_variant, model?.locationInfo);
        if (minPriceVariantOnRoad) {
            min_onRoad_price = minPriceVariantOnRoad.onRoadPrice || 0;
        }
    }

    // Title and description with model tag conversion
    const baseTitle = h1Title ? h1Title : model?.h1_title
        ? convertModelTags(model.h1_title, model)
        : `${model?.brand_name || ''} ${model?.name || ''}${isPriceInCityRoute ? (model?.locationInfo?.name ? ` Price in ${model?.locationInfo?.name}` : ' price') : ''}`;
    const title = isPriceInCityRoute && min_onRoad_price > 0
        ? `${baseTitle} - Starts at ${formatPrice(min_onRoad_price)}`
        : baseTitle;
    const rawDescription = model?.top_description || model?.description || "";

    const description = topDescription ? topDescription : isPriceInCityRoute
        ? convertModelTags(isUpcoming ? modelLocationTopContent.upcoming : isElectric ? modelLocationTopDescriptionContent.EV : modelLocationTopDescriptionContent.nonEV, model)
        : (isEmptyDescription(rawDescription)
            ? convertModelTags(isElectric ? modelDynamicTopContent.EV : modelDynamicTopContent.nonEV, model)
            : convertModelTags(rawDescription, model)
        );

    // Fetch variants
    useEffect(() => {
        let mounted = true;
        const fetch = async () => {
            try {
                if (!model?._id) return;
                if (prefetchedVariants.length > 0) return;
                const res: any = await getVariantList(model._id, 1, 1000);
                const list = res?.data || [];
                if (!mounted) return;
                if (Array.isArray(list) && list.length > 0) setVariants(list);
                if ((!selectedVariant || !selectedVariant.name) && Array.isArray(list) && list.length > 0) {
                    setSelectedVariant(list[0]);
                }
            } catch (e) {
                // silent
            }
        };
        fetch();
        return () => { mounted = false; };
    }, [model?._id, prefetchedVariants.length]);

    useEffect(() => {
        let mounted = true;

        const fetchReviews = async () => {
            // Skip client fetch if server prefetched reviews
            if (prefetchedReviewRating !== undefined && prefetchedReviewCount !== undefined) return;
            try {
                setIsFetchingReviews(true);
                if (!model?._id) return;
                const res = await getAllUserReviews({ model_id: model._id, limit: 50, page: 1 });
                if (!mounted) return;

                if (Array.isArray(res?.reviews) && res.reviews.length > 0) {
                    const numericRatings = res.reviews
                        .map((item: any) => Number(item?.rating))
                        .filter((value: number) => !Number.isNaN(value) && value > 0);

                    const avgRating = numericRatings.length > 0
                        ? numericRatings.reduce((sum: number, value: number) => sum + value, 0) / numericRatings.length
                        : 0;

                    setReviewRating(avgRating);
                    setReviewCount(Number(res?.pagination?.total ?? res.reviews.length));
                    return;
                }

                setReviewRating(Number(model?.user_rating ?? 0));
                setReviewCount(Number(model?.reviews_count ?? 0));
            } catch (e) {
                if (!mounted) return;
                setReviewRating(Number(model?.user_rating ?? 0));
                setReviewCount(Number(model?.reviews_count ?? 0));
            } finally {
                setIsFetchingReviews(false);
            }
        };

        fetchReviews();
        return () => { mounted = false; };
    }, [model?._id, model?.user_rating, model?.reviews_count]);

    const { emiData, isEmiOpen, emiSelectedVariant, emiOnRoadPrices, handleOpenEmiCalculator, setIsEmiOpen, setEmiSelectedVariant } = useEmiData({
        model,
        selectedVariant,
        variants,
        selectedCity,
        isPriceInCityRoute,
        rtoResult,
        insuranceResult,
        isRtoLoading,
        isInsuranceLoading,
        price,
    });

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (variantDropdownRef.current && !variantDropdownRef.current.contains(event.target as Node)) {
                setShowVariantDropdown(false);
            }
        };

        if (showVariantDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showVariantDropdown]);

    const handleOpenLocation = () => {
        window.dispatchEvent(new CustomEvent('open-location-modal'));
    };


    const sliderData = { ...model, images: model?.images ?? [], is_discontinued: isDiscontinued } as any;


    // find current variant details
    const currentVariant = variants?.find((v: any) => v.slug?.trim() === variantSlug?.trim());

    const brandSlug = data?.brand_slug || data?.brand?.slug || '';
    // variants options
    const currentVariantOption = {
        label: `${model?.name || ''} ${currentVariant?.name || ''}`.trim(),
        slug: currentVariant?.slug,
        url: `/${brandSlug}/${data?.slug}/${currentVariant?.slug}`
    }
    const otherVariants = variants?.filter((v: any) => v.slug?.trim() !== variantSlug?.trim() && v.status !== 'upcoming' && v.is_sanity_done)
        .map((v: any) => ({
            label: `${model?.name || ''} ${v.name || ''}`.trim(),
            slug: v.slug,
            url: `/${brandSlug}/${data?.slug}/${v.slug}`
        }));

    const variantsOptions = [currentVariantOption, ...otherVariants];

    return (
        <section className="overflow-visible" data-testid="mobile-hero-section">
            {/* Title + badges */}
            <div>
                <div className="flex items-center justify-between gap-2 mb-4">
                    {titleTag === "h2" ? (
                        <h2 className="text-2xl font-semibold text-gray-900">
                            {title}
                        </h2>
                    ) : (
                        <h1 className="text-2xl font-semibold text-gray-900">
                            {title}
                        </h1>
                    )}
                </div>

                {!isPriceInCityRoute && !hideReviewRating && reviews > 0 && (
                    <div className="flex items-center justify-between gap-2">
                        <div className="hero-review-rating">
                            <span className="inline-flex items-center gap-2 px-2 py-1 bg-[#EFF6FF] rounded-[10px] text-[14px] text-gray-700">
                                <Image
                                    src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}yellow-star.svg`}
                                    alt="star"
                                    title="star"
                                    width={12}
                                    height={12}
                                    className="w-[12px] h-[12px] filter invert"
                                />
                                <span className="text-[#1447E6]">{rating.toFixed(1)}</span>
                                {/* <span className="text-[#4A5565]">User ({reviews})</span> */}
                            </span>
                        </div>
                    </div>
                )}

                {/* Description (CLS-safe) */}
                <div className="mt-3">
                    {description && !isPriceInCityRoute && (
                        <BodyContent
                            fullText={description}
                            containerClassName="mb-6"
                            className="line-clamp-3"
                        />
                    )}
                </div>
            </div>

            {/* Image slider — aspect ratio is handled inside SliderWithThumbnails */}
            <div className="mt-1 w-full">
                <SliderWithThumbnails
                    data={sliderData}
                    galleryData={galleryData}
                    priority={priority}
                    isMobile={isMobile}
                />
            </div>

            {/* Price + CTA */}
            <div className={`mt-4 rounded-xl border border-gray-200  overflow-visible ${!isVariantRoute ? '' : ''}`}>
                {isPriceInCityRoute && (
                    <>
                        {model?.price_range?.base &&
                            <div className="mt-3 flex gap-2 relative mx-2">
                                {/* Variant Card */}
                                {variants.length > 0 && (
                                    <div
                                        ref={variantDropdownRef}
                                        className="flex-1 rounded-lg border border-gray-200 p-2.5 relative"
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => setShowVariantDropdown(s => !s)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') setShowVariantDropdown(s => !s); }}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-xs text-gray-500">Variant</p>
                                                <div className="flex items-center gap-2">
                                                    <p className="text-sm font-semibold text-gray-900">
                                                        {selectedVariant?.name ? `${model?.name || ''} ${selectedVariant.name}`.trim() : "Base"}
                                                    </p>
                                                    {selectedVariant?.is_top_variant && (
                                                        <span className="text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded">Top</span>
                                                    )}
                                                    {selectedVariant?.is_base_variant && (
                                                        <span className="text-[10px] bg-gray-100 text-gray-700 px-2 py-0.5 rounded">Base</span>
                                                    )}
                                                </div>
                                            </div>
                                            <span className="text-gray-400 text-xl">›</span>
                                        </div>

                                        {/* Variant Dropdown */}
                                        {showVariantDropdown && isPriceInCityRoute && variants.length > 0 && (
                                            <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-2xl border border-gray-200 max-h-[320px] overflow-y-auto z-50 w-[calc(200%+0.5rem)]">
                                                <div className="space-y-1 p-2">
                                                    {variants.filter((v: any) => v?.status !== 'discontinued').map((v: any, idx: number) => {
                                                        const info = extractCcAndFuel(v);
                                                        const isSelected = selectedVariant && (selectedVariant._id === v._id || selectedVariant.name === v.name);
                                                        return (
                                                            <div
                                                                key={v._id || v.name || idx}
                                                                role="button"
                                                                tabIndex={0}
                                                                onClick={(e) => { e.stopPropagation(); setSelectedVariant(v); setShowVariantDropdown(false); saveSelVariantCookie(model?.slug, v?.slug); router.refresh(); }}
                                                                onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setSelectedVariant(v); setShowVariantDropdown(false); saveSelVariantCookie(model?.slug, v?.slug); router.refresh(); } }}
                                                                className={`flex items-start justify-between gap-2 px-2 py-2 cursor-pointer transition-colors border-b border-gray-200 hover:bg-blue-100 ${isSelected ? 'bg-blue-50 border border-blue-100' : 'hover:bg-gray-50'}`}
                                                            >
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="flex items-center gap-2">
                                                                        <div className="text-[13px] font-medium text-gray-900 truncate">{model?.name} {v.name}</div>
                                                                        {v?.is_top_variant && (
                                                                            <span className="text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded flex-shrink-0">Top</span>
                                                                        )}
                                                                        {v?.is_base_variant && (
                                                                            <span className="text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded flex-shrink-0">Base</span>
                                                                        )}
                                                                    </div>
                                                                    <div className="flex items-center justify-between mt-1">
                                                                        <div className="text-[12px] text-gray-500">{info.cc}{info.cc && info.fuel ? ' • ' : ''}{info.fuel}</div>
                                                                        <div className="text-[12px] text-gray-500 text-right">{v.price ? formatPrice(Number(v.price)) : ''}</div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* City Card - only show on price-in-city route */}
                                {isPriceInCityRoute && (
                                    <div className="flex-1 rounded-lg border border-gray-200 p-2.5"
                                        onClick={handleOpenLocation} role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => { if (e.key === 'Enter') handleOpenLocation(); }}>
                                        <div className="flex items-center justify-between">
                                            <div className="">
                                                <p className="text-xs text-gray-500 mb-1">City</p>
                                                <p className="text-[12px] text-[#155DFC] font-semibold flex items-center gap-2 cursor-pointer">
                                                    {selectedCity}
                                                    <Image
                                                        src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}pencil.svg`}
                                                        alt="Edit location"
                                                        title="Edit location"
                                                        width={12}
                                                        height={12}
                                                        className="w-3 h-3"
                                                    />
                                                </p>
                                            </div>
                                            <span className="text-gray-400 text-xl">›</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        }

                        <div className="px-3 py-2.5">
                            {model?.price_range?.base &&
                                <p className="font-roboto font-bold text-[13px] leading-[17.33px] tracking-[0px] mb-2">Price Summary</p>
                            }
                            <div className="hide-price">
                                {model?.price_range?.base ?
                                    <table className="w-full">
                                        <tbody>
                                            <tr>
                                                <td className="pb-2 align-middle font-roboto font-normal text-[12px] leading-[16px] tracking-[0px]">
                                                    {isUpcoming && model?.price_range?.base ? "Estimated " : ""}Ex-Showroom Price
                                                </td>
                                                <td className="pb-2 text-right align-middle font-roboto font-normal text-[12px] leading-[16px] tracking-[0px]">
                                                    {(selectedVariant?.price || price) ? `₹ ${Number(selectedVariant?.price || price).toLocaleString('en-IN')}` : (isUpcoming ? 'Price to be Announced' : 'Price on request')}
                                                </td>
                                            </tr>
                                            {isUpcoming ?
                                                <tr>
                                                    <td className="pb-2 align-middle font-roboto font-normal text-[12px] leading-[16px] tracking-[0px]">Estimated Other Charges</td>
                                                    <td className="pb-2 text-right align-middle font-roboto font-normal text-[12px] leading-[16px] tracking-[0px] select-none pointer-events-none">
                                                        {(rtoResult || insuranceResult)
                                                            ? `₹ ${Number((rtoResult?.total || 0) + (insuranceResult?.total || 0)).toLocaleString('en-IN')}`
                                                            : ''}
                                                    </td>
                                                </tr>
                                                :
                                                <>
                                                    <tr>
                                                        <td className="pb-2 align-middle font-roboto font-normal text-[12px] leading-[16px] tracking-[0px]">RTO, Registration </td>
                                                        <td className="pb-2 text-right align-middle font-roboto font-normal text-[12px] leading-[16px] tracking-[0px] select-none pointer-events-none">
                                                            {rtoResult ? `₹ ${Number(rtoResult.total).toLocaleString('en-IN')}` : ''}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td className="pb-2 align-middle font-roboto font-normal text-[12px] leading-[16px] tracking-[0px]">Insurance</td>
                                                        <td className="pb-2 text-right align-middle font-roboto font-normal text-[12px] leading-[16px] tracking-[0px] select-none pointer-events-none">
                                                            {insuranceResult ? `₹ ${Number(insuranceResult.total).toLocaleString('en-IN')}` : ''}
                                                        </td>
                                                    </tr>
                                                </>
                                            }
                                            <tr>
                                                <td className="border-t border-gray-200 pt-2 align-middle font-roboto font-normal text-[12px] leading-[16px] tracking-[0px]">
                                                    {isUpcoming ? "Estimated " : "Total "}On-Road Price
                                                </td>
                                                <td className="border-t border-gray-200 pt-2 text-right align-middle font-roboto font-normal text-[12px] leading-[16px] tracking-[0px] select-none pointer-events-none">
                                                    {(rtoResult || insuranceResult)
                                                        ? `₹ ${Number((selectedVariant?.price || price) + (rtoResult?.total || 0) + (insuranceResult?.total || 0)).toLocaleString('en-IN')}`
                                                        : ''}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                    :
                                    <div className="px-3 py-3 min-h-[96px]">
                                        <div className="bg-[#008C8B0D] rounded-xl p-3">
                                            <p className="font-roboto font-bold text-[20px] leading-[28.8px] text-[#008C8B] mb-1">
                                                Price to be Announced
                                            </p>
                                            <p className="text-sm text-gray-700">
                                                Expected Date of Launch :{" "}
                                                <b>{model?.launch_date ? dateFormat(model?.launch_date) : "Coming Soon"}</b>
                                            </p>
                                        </div>
                                    </div>
                                }
                            </div>
                        </div>

                        {emiData && !isRtoLoading && !isInsuranceLoading && !isDiscontinued && (
                            <EmiStartCard
                                onClick={handleOpenEmiCalculator}
                                emiAmount={emiData.amount}
                                tooltipText={emiData.tooltip}
                                TrackingEMI={EmiStartCardTracking}
                                brand={model?.brand_name}
                                model={model?.name}
                            />
                        )}

                        <div className="px-3 pb-2.5">
                            {isDiscontinued ? (
                                <p className="flex-1 p-3 text-[14px] border border-red-500 text-red-500 bg-red-50 rounded-lg text-left">
                                    This {isVariantRoute ? "variant" : 'model'} has been discontinued.
                                </p>
                            ) : (
                                <LeadButton
                                    carId={model?._id}
                                    variantId={model?.variant_id}
                                    carName={title}
                                    // btnText={
                                    //     leadCTAText ? leadCTAText : isUpcoming ? "Notify Me" : isPriceInCityRoute ? "Get Offers" : "Get on road Price"
                                    // }
                                    btnText={
                                        leadCTAText ? leadCTAText : isUpcoming ? "Notify Me" : isPriceInCityRoute ? "View Diwali Offers" : "Get on road Price"
                                    }
                                    {...(GORPFormHeading ?
                                        { formHeading: GORPFormHeading }
                                        :
                                        isPriceInCityRoute
                                            ? {
                                                formHeading: (
                                                    <>
                                                        Get on road Price for{" "}
                                                        <span className="font-semibold text-[#008C8B]">
                                                            {model?.brand_name} {model?.name}
                                                        </span>{" "}
                                                        in{" "}
                                                        <span className="font-semibold text-[#008C8B]">
                                                            {selectedCity}
                                                        </span>
                                                    </>
                                                )
                                            }
                                            : {})}
                                    useSplashPopup={useSplashPopup || isPriceInCityRoute}
                                    className="w-full rounded-lg bg-teal-600 text-white py-2.5 font-semibold"
                                    is_upcoming={isUpcoming}
                                />
                            )}
                        </div>

                        {/* Book Test Drive Component */}
                        {/* {!isDiscontinued && isPriceInCityRoute && (
                            <BookTestDrive vehicleName={`${model?.brand_name} ${model?.name}`} carId={model?._id} />
                        )} */}
                    </>
                )}

                {!isPriceInCityRoute && (
                    <>
                        {!isSpecPage &&
                            <div className="mt-3 px-3 py-3 pb-0.5">
                                {!isVariantRoute ? (
                                    <div className="rounded-lg border border-gray-200 p-2.5">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-xs text-gray-500">
                                                    Variant
                                                </p>
                                                <p className="text-sm font-semibold text-gray-900">
                                                    {(selectedVariant?.name || variant?.name)
                                                        ? `${model?.name || ''} ${selectedVariant?.name || variant?.name}`.trim()
                                                        : "Base"}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className='my-3 mx-2'>
                                            <VariantSelect options={variantsOptions} defaultSelected={currentVariantOption} title={`Choose ${model?.brand_name} ${model?.name} Variant`} />
                                        </div>
                                    </>
                                )}
                            </div>
                        }

                        <div className={`px-3 py-3 min-h-[96px] ${!isVariantRoute ? '' : 'bg-[#008C8B0D] border-[0.4px] border-[#008C8B26] rounded-lg p-4 my-3 mx-2'}`}>
                            {hasDisplayPrice ? (
                                <div className="mb-3">
                                    <p className="text-sm text-gray-500">
                                        {isUpcoming ? "Estimated Price" : "Ex-Showroom Price"}
                                    </p>
                                    <span className="soab text-gray-900" dangerouslySetInnerHTML={{ __html: displayPrice }}></span>
                                    {isPDPRoute && (
                                        <Link prefetch={false} href={`/${brandSlug}/${data?.slug}/price-in-${prefetchedCitySlug || ''}`} className="text-[14px] text-[#155DFC] leading-[20px] font-semibold ml-4 md:ml-2">
                                            Get On Road Price
                                        </Link>
                                    )}

                                    {isUpcoming && (
                                        <p className="text-sm text-gray-700 mt-2">
                                            Expected Date of Launch :{" "}
                                            <b>{model?.launch_date ? dateFormat(model?.launch_date) : "Coming Soon"}</b>
                                        </p>
                                    )}
                                </div>
                            ) : (
                                <div className="bg-[#008C8B0D] rounded-xl p-3">
                                    <p className="font-roboto font-bold text-[20px] leading-[28.8px] text-[#008C8B] mb-1">
                                        Price to be Announced
                                    </p>
                                    <p className="text-sm text-gray-700">
                                        Expected Date of Launch :{" "}
                                        <b>{model?.launch_date ? dateFormat(model?.launch_date) : "Coming Soon"}</b>
                                    </p>
                                </div>
                            )}
                        </div>

                        {emiData && !isRtoLoading && !isInsuranceLoading && !isDiscontinued && (
                            <EmiStartCard
                                onClick={handleOpenEmiCalculator}
                                emiAmount={emiData.amount}
                                tooltipText={emiData.tooltip}
                                TrackingEMI={EmiStartCardTracking}
                                brand={model?.brand_name}
                                model={model?.name}
                            />
                        )}

                        <div className="border-t border-gray-200 px-3 py-3" ref={offerBtnRef}>
                            {isDiscontinued ? (
                                <p className="flex-1 p-3 text-[14px] border border-red-500 text-red-500 bg-red-50 rounded-lg text-left">
                                    This {isVariantRoute ? "variant" : 'model'} has been discontinued.
                                </p>
                            ) : (
                                <LeadButton
                                    carId={model?._id}
                                    variantId={model?.variant_id}
                                    carName={title}
                                    // btnText={leadCTAText ? leadCTAText : isUpcoming
                                    //     ? "Notify Me"
                                    //     : `View ${new Date().toLocaleString('en-IN', { month: 'long' })} Offers`
                                    // }
                                    btnText={leadCTAText ? leadCTAText : isUpcoming
                                        ? "Notify Me"
                                        : `View Diwali Offers`
                                    }
                                    {...(GORPFormHeading ?
                                        { formHeading: GORPFormHeading }
                                        :
                                        isPriceInCityRoute
                                            ? {
                                                formHeading: (
                                                    <>
                                                        Get on road Price for{" "}
                                                        <span className="font-semibold text-[#008C8B]">
                                                            {model?.brand_name} {model?.name}
                                                        </span>{" "}
                                                        in{" "}
                                                        <span className="font-semibold text-[#008C8B]">
                                                            {selectedCity}
                                                        </span>
                                                    </>
                                                )
                                            }
                                            : {})}
                                    useSplashPopup={useSplashPopup || isPriceInCityRoute}
                                    className={`w-full rounded-lg bg-teal-600 text-white py-3 font-semibold ${gorpTrackingClass}`}
                                    is_upcoming={isUpcoming}
                                />
                            )}
                        </div>
                        {!isDiscontinued && (
                            <MobileStickyCta anchorRef={offerBtnRef}>
                                <LeadButton
                                    carId={model?._id}
                                    variantId={model?.variant_id}
                                    carName={title}
                                    // btnText={leadCTAText ? leadCTAText : isUpcoming
                                    //     ? "Notify Me"
                                    //     : `View ${new Date().toLocaleString('en-IN', { month: 'long' })} Offers`
                                    // }
                                    btnText={leadCTAText ? leadCTAText : isUpcoming
                                        ? "Notify Me"
                                        : `View Diwali Offers`
                                    }
                                    {...(GORPFormHeading ? { formHeading: GORPFormHeading } : {})}
                                    useSplashPopup={useSplashPopup}
                                    className={`${MOBILE_STICKY_CTA_CLASS} ${gorpTrackingClass}`}
                                    is_upcoming={isUpcoming}
                                />
                            </MobileStickyCta>
                        )}
                    </>
                )}
            </div>

            {isEmiOpen && (
                <EmiCalculator
                    carName={`${model?.brand_name} ${model?.name}`}
                    variants={variants}
                    onRoadPrices={Object.keys(emiOnRoadPrices).length > 0 || (selectedVariant?._id && rtoResult && insuranceResult)
                        ? {
                            ...emiOnRoadPrices,
                            ...(selectedVariant?._id && rtoResult && insuranceResult
                                ? {
                                    [selectedVariant._id]: (selectedVariant?.price || price) + rtoResult.total + insuranceResult.total
                                }
                                : {})
                        }
                        : undefined}
                    cityName={selectedCity !== 'Change Location' ? selectedCity : undefined}
                    isMobile={isMobile}
                    selectedVariantId={emiSelectedVariant?._id}
                    onVariantChange={(nextVariant) => setEmiSelectedVariant(nextVariant)}
                    isOpen={isEmiOpen}
                    onClose={() => setIsEmiOpen(false)}
                    VarentSectorClass={PDP_EMICALCULATOR_POPUP_TRACKING.PDP_VARIANT_SELECTOR_CLICK}
                    DownPaymentClass={PDP_EMICALCULATOR_POPUP_TRACKING.PDP_DOWN_PAYMENT_CLICK}
                    LoanTermClass={PDP_EMICALCULATOR_POPUP_TRACKING.PDP_LOAN_PERIOD_CLICK}
                    InterestRateClass={PDP_EMICALCULATOR_POPUP_TRACKING.PDP_INTEREST_RATE_CLICK}

                />
            )}

            {isLocationModalOpen && (
                <LocationSelectModal
                    showModal={isLocationModalOpen}
                    onClose={() => setIsLocationModalOpen(false)}
                    onSelectCity={(city) => {
                        setSelectedCity(city);
                        setIsLocationModalOpen(false);
                        handleOpenEmiCalculator();
                    }}
                />
            )}
        </section>
    );
};

export default HeroSectionMobile;
